import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Chapter, Scenario, Scene } from '../types';
import styles from './developer-menu.module.css';

interface ChapterSceneGroup {
  chapter: Chapter | null;
  scenes: Scene[];
}

function groupScenesByChapter(scenario: Scenario): ChapterSceneGroup[] {
  const { scenes } = scenario;
  const chapters = scenario.metadata.chapters;

  if (!chapters || chapters.length === 0) {
    return [{ chapter: null, scenes: [...scenes] }];
  }

  const chapterStarts = chapters
    .map((chapter) => ({
      chapter,
      index: scenes.findIndex((scene) => scene.id === chapter.startScene),
    }))
    .filter((entry) => entry.index >= 0)
    .sort((a, b) => a.index - b.index);

  const groups: ChapterSceneGroup[] = [];

  if (chapterStarts.length > 0 && chapterStarts[0]!.index > 0) {
    groups.push({
      chapter: null,
      scenes: scenes.slice(0, chapterStarts[0]!.index),
    });
  }

  for (let i = 0; i < chapterStarts.length; i++) {
    const start = chapterStarts[i]!.index;
    const end = i + 1 < chapterStarts.length ? chapterStarts[i + 1]!.index : scenes.length;
    groups.push({
      chapter: chapterStarts[i]!.chapter,
      scenes: scenes.slice(start, end),
    });
  }

  return groups;
}

function getScenePreview(scene: Scene): string | null {
  if (scene.type === 'dialogue' && scene.text) {
    const text = scene.text.replace(/\s+/g, ' ').trim();
    if (text.length > 0) {
      return text.length > 72 ? `${text.slice(0, 72)}…` : text;
    }
  }
  return null;
}

export interface DeveloperMenuProps {
  scenario: Scenario;
  currentSceneId?: string;
  onNavigateToScene: (sceneId: string) => void;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  currentBadgeLabel?: string;
}

export function DeveloperMenu({
  scenario,
  currentSceneId,
  onNavigateToScene,
  onClose,
  title = 'Menu développeur',
  subtitle = 'Naviguez vers n\'importe quelle scène · Ctrl+K',
  currentBadgeLabel = 'Actuelle',
}: DeveloperMenuProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const chapterGroups = useMemo(() => groupScenesByChapter(scenario), [scenario]);

  const filteredGroups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return chapterGroups;

    return chapterGroups
      .map((group) => ({
        ...group,
        scenes: group.scenes.filter((scene) => {
          const preview = getScenePreview(scene);
          return (
            scene.id.toLowerCase().includes(normalizedQuery)
            || (preview?.toLowerCase().includes(normalizedQuery) ?? false)
          );
        }),
      }))
      .filter((group) => group.scenes.length > 0);
  }, [chapterGroups, query]);

  const flatScenes = useMemo(
    () => filteredGroups.flatMap((group) => group.scenes),
    [filteredGroups],
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (flatScenes.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % flatScenes.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + flatScenes.length) % flatScenes.length);
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const scene = flatScenes[activeIndex];
        if (scene) {
          onNavigateToScene(scene.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, flatScenes, onClose, onNavigateToScene]);

  useEffect(() => {
    const activeScene = flatScenes[activeIndex];
    if (!activeScene) return;
    const element = document.getElementById(`dev-scene-${activeScene.id}`);
    element?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, flatScenes]);

  const handleSceneClick = useCallback((sceneId: string) => {
    onNavigateToScene(sceneId);
  }, [onNavigateToScene]);

  let runningIndex = 0;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="developer-menu-title"
    >
      <div className={styles.panel} onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2 id="developer-menu-title" className={styles.title}>{title}</h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fermer le menu développeur"
          >
            ✕
          </button>
        </div>

        <input
          ref={inputRef}
          type="search"
          className={styles.searchInput}
          placeholder="Rechercher une scène…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Rechercher une scène"
        />

        <div className={styles.sceneList}>
          {filteredGroups.length === 0 && (
            <p className={styles.emptyState}>Aucune scène trouvée.</p>
          )}

          {filteredGroups.map((group) => (
            <section key={group.chapter?.id ?? 'uncategorized'} className={styles.chapterSection}>
              <h3 className={styles.chapterTitle}>
                {group.chapter?.title ?? 'Hors chapitre'}
              </h3>
              <ul className={styles.sceneItems}>
                {group.scenes.map((scene) => {
                  const itemIndex = runningIndex;
                  runningIndex += 1;
                  const isActive = itemIndex === activeIndex;
                  const isCurrent = scene.id === currentSceneId;
                  const preview = getScenePreview(scene);

                  return (
                    <li key={scene.id}>
                      <button
                        id={`dev-scene-${scene.id}`}
                        type="button"
                        className={`${styles.sceneButton} ${isActive ? styles.sceneButtonActive : ''} ${isCurrent ? styles.sceneButtonCurrent : ''}`}
                        onClick={() => handleSceneClick(scene.id)}
                        onMouseEnter={() => setActiveIndex(itemIndex)}
                      >
                        <span className={styles.sceneId}>{scene.id}</span>
                        {preview && <span className={styles.scenePreview}>{preview}</span>}
                        {isCurrent && <span className={styles.currentBadge}>{currentBadgeLabel}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
